import { Link, createFileRoute } from '@tanstack/react-router'
import { Badge, Card } from '@react-spectrum/s2'
import { Building, Users } from 'lucide-react'
import { getAdminOrganizations } from '@rpc/admin.rpc'
import { fullWidthStyles } from '@/styles/s2-macros'

export const Route = createFileRoute('/admin/organizations/')({
  loader: async () => {
    return { organizations: await getAdminOrganizations() }
  },
  component: OrganizationsPage,
})

function OrganizationsPage() {
  const { organizations } = Route.useLoaderData()

  return (
    <div className="mx-auto w-full max-w-6xl space-y-8 px-4 py-8 sm:px-6 lg:px-8">
      <div>
        <h2 className="text-3xl font-bold text-neutral-900 dark:text-neutral-100">
          Organization management
        </h2>
        <p className="text-neutral-500">
          Overview of all registered organizations and teams.
        </p>
      </div>

      <Card styles={fullWidthStyles}>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-neutral-200 bg-neutral-50/80 dark:border-neutral-800 dark:bg-neutral-900/70">
              <tr>
                <th className="px-6 py-4 font-semibold text-neutral-700 dark:text-neutral-300">
                  Name
                </th>
                <th className="px-6 py-4 font-semibold text-neutral-700 dark:text-neutral-300">
                  Slug
                </th>
                <th className="px-6 py-4 font-semibold text-neutral-700 dark:text-neutral-300">
                  Members
                </th>
                <th className="px-6 py-4 font-semibold text-neutral-700 dark:text-neutral-300">
                  Plan
                </th>
                <th className="px-6 py-4 font-semibold text-neutral-700 dark:text-neutral-300">
                  Created
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100 dark:divide-neutral-800">
              {organizations.length === 0 && (
                <tr>
                  <td
                    colSpan={5}
                    className="px-6 py-10 text-center text-neutral-500 italic"
                  >
                    No organizations found.
                  </td>
                </tr>
              )}
              {organizations.map((org: any) => {
                let metadata: any = {}
                try {
                  metadata = org.metadata ? JSON.parse(org.metadata) : {}
                } catch (e) {
                  /* ignore */
                }

                return (
                  <tr
                    key={org.id}
                    className="transition-colors hover:bg-neutral-50/70 dark:hover:bg-neutral-900/40"
                  >
                    <td className="px-6 py-4">
                      <Link
                        to="/admin/organizations/$orgId"
                        params={{ orgId: org.id }}
                        className="flex items-center gap-3 group"
                      >
                        <div className="h-8 w-8 rounded bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400 group-hover:bg-blue-200 transition-colors">
                          <Building className="h-4 w-4" />
                        </div>
                        <span className="font-medium group-hover:text-blue-600 transition-colors">
                          {org.name}
                        </span>
                      </Link>
                    </td>
                    <td className="px-6 py-4 font-mono text-xs text-neutral-500">
                      {org.slug}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1.5">
                        <Users className="h-3 w-3 text-neutral-400" />
                        <span>{org.memberCount}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <Badge variant="neutral" fillStyle="outline">
                        {formatPlanLabel(metadata.plan)}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 text-neutral-500">
                      {new Date(org.createdAt).toLocaleDateString()}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  )
}

function formatPlanLabel(plan?: string) {
  if (!plan) return 'Free'
  return plan.charAt(0).toUpperCase() + plan.slice(1)
}
